import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import { getMe } from '../../redux/actions/meAction'
import CustomInput from '../UI/CustomInput/CustomInput'
import CustomTextArea from '../UI/CustomTextArea/CustomTextArea'
import { getCompanies } from '../../redux/actions/companiesAction'
import { categoriesPerPage, companiesPerPage } from '../../constants/constants'
import { useRequestStatus } from '../../hooks/useRequestStatus'
import IconTrashRed from '../../assets/icons/IconTrashRed.svg'
import { Formik, Form, Field, FormikHelpers, FormikValues } from 'formik'
import * as yup from 'yup'

import styles from './CreateCategoryModal.module.css'
import CustomButton from '../UI/CustomButton/CustomButton'
import close from '../../assets/icons/close.svg'
import {
  changeCategoryMainInformation,
  createCategory,
  deleteCurrentCategory,
} from '../../redux/actions/categoriesAction'
import s from '../../styles/common/Form.module.css'
import { CategoriesType } from '../../redux/actions/types'
import Select from 'react-select'
import DeleteComponent from '../DeleteComponent/DeleteComponent'
import CustomModal from '../UI/CustomModal/CustomModal'
import { useMediaQuery } from 'react-responsive'
import { useToast } from '../../context/ToastContext'
import { getCurrentMenu } from '../../redux/actions/menuAction'
import { useParams } from 'react-router-dom'

interface CreateCategoryModalProps {
  currentCat?: CategoriesType
  isEditMode: boolean
  isOpen: boolean
  onClose: () => void
  menuId: string
}

const CATEGORY_TYPES = [
  { value: 'food', label: 'Еда и напитки' },
  { value: 'rent', label: 'Аренда оборудования' },
  { value: 'alcohol', label: 'Алкогольные напитки' },
]

const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({
  isOpen,
  onClose,
  currentCat,
  isEditMode,
  menuId,
}) => {
  const dispatch = useAppDispatch()
  const [companyId, setCompanyId] = useState<number>(-1)
  const [currentPage, setCurrentPage] = useState(1)
  const { showSuccess, showError } = useToast()
  const { companies } = useAppSelector((state) => state.companies)
  const { meData } = useAppSelector((state) => state.me)
  const { userRole, token } = useAppSelector((state) => state.auth)
  const { id } = useParams()
  const [loaded, setLoaded] = useState<boolean>(false)

  const [visibleDelete, setVisibleDelete] = useState<boolean>(false)

  const { error } = useAppSelector((state) => state.categories)

  const debouncedStatus = useRequestStatus('')

  useEffect(() => {
    if (userRole !== 'superAdmin' && userRole !== 'admin') {
      dispatch(getMe({ token }))
    }
  }, [dispatch, token, userRole])

  useEffect(() => {
    dispatch(
      getCompanies({
        token,
        page: currentPage,
        itemsPerPage: companiesPerPage,
        role: debouncedStatus,
      }),
    )
  }, [dispatch, currentPage, debouncedStatus, token])

  useEffect(() => {
    if (userRole !== 'superAdmin' && userRole !== 'admin' && meData?.company) {
      setCompanyId(meData.company.id)
    }
  }, [meData, userRole])

  const validationSchema = yup.object().shape({
    menuName: yup.string().required('Название категории обязательно'),
    companyId: yup.number().required('Компания обязательна'),
    categoryType: yup.string().required('Тип категории обязателен'),
  })
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
  const handleSubmitHandler = async (
    values: FormikValues,
    { setSubmitting, setErrors, resetForm }: FormikHelpers<any>,
  ) => {
    const _companyId =
      userRole === 'superAdmin' || userRole === 'admin' ? companyId : meData.company.id

    const requestData = {
      name: values.menuName,
      description: values.description,
      companyId: String(_companyId),
      type: values.categoryType,
    }

    setLoaded(true)
    const resultAction = await dispatch(createCategory({ data: requestData, token, menuId }))

    if (createCategory.fulfilled.match(resultAction)) {
      showSuccess('Категория успешно создана')
      setLoaded(false)
      resetForm()
      onClose()
      dispatch(getCurrentMenu({ menuId: id, token }))
    } else {
      const errorMessage = resultAction.error?.message || 'Ошибка при создании категории'
      showError('Ошибка при создании категории')
      setErrors({ menuName: errorMessage })
      setLoaded(false)
    }

    setSubmitting(false)
  }

  const handleSubmitHandlerEdit = async (
    values: FormikValues,
    { setSubmitting, setErrors, resetForm }: FormikHelpers<any>,
  ) => {
    const requestData = {
      name: values.menuName,
      description: values.description,
      categoryId: currentCat && String(currentCat.id),
      type: values.categoryType,
    }

    setLoaded(true)
    const resultActionMainInfo = await dispatch(
      changeCategoryMainInformation({
        token,
        categoryId: requestData.categoryId as string,
        name: requestData.name,
        description: requestData.description,
        type: requestData.type,
      }),
    )

    if (changeCategoryMainInformation.fulfilled.match(resultActionMainInfo)) {
      showSuccess('Категория успешно изменена')
      dispatch(getCurrentMenu({ menuId: id, token }))
      setLoaded(false)
      resetForm()
      onClose()
    } else {
      const errorMessage = resultActionMainInfo.error?.message || 'Ошибка при изменении категории'
      showError('Ошибка при изменении категории')
      setErrors({ menuName: errorMessage })
      setLoaded(false)
    }

    setSubmitting(false)
  }

  if (!isOpen) return null

  const onDeletePlatformHandler = async () => {
    const resultAction = await dispatch(
      deleteCurrentCategory({ categoryId: currentCat && String(currentCat.id), token }),
    )
    setVisibleDelete(false)
    if (deleteCurrentCategory.rejected.match(resultAction)) {
      showError('Ошибка при удалении категории')
    } else {
      showSuccess('Категория успешно удалена')
      onClose()
      dispatch(getCurrentMenu({ menuId: id, token }))
    }
  }

  return (
    <div className={styles.createMenuModalOverlay}>
      <CustomModal visible={visibleDelete} setVisible={setVisibleDelete}>
        <DeleteComponent
          title='Удаление категории'
          description='Хотите удалить категорию'
          onCancelHandler={() => setVisibleDelete(false)}
          onDeleteHandler={onDeletePlatformHandler}
        />
      </CustomModal>
      <div className={styles.createMenuModal}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>
            {isEditMode ? 'Изменить категорию' : 'Создание категории'}
          </h1>
          <img onClick={onClose} className={styles.close} src={close} alt='close' />
        </div>

        <Formik
          initialValues={{
            menuName: isEditMode ? (currentCat && currentCat?.name) || '' : '',
            description: isEditMode ? (currentCat && currentCat?.description) || '' : '',
            companyId: companyId,
            categoryType: isEditMode ? (currentCat && (currentCat as any)?.type) || '' : '',
          }}
          validationSchema={validationSchema}
          onSubmit={isEditMode ? handleSubmitHandlerEdit : handleSubmitHandler}
        >
          {({ values, touched, setFieldValue, errors, handleChange, handleBlur }) => (
            <Form className={styles.inputBlocks}>
              <div className={styles.wrapperFields}>
                <div className={styles.inputBlock}>
                  <div className={styles.leftSide}>
                    <h4>Название</h4>
                  </div>
                  <div className={styles.rightSide}>
                    <CustomInput
                      type='text'
                      placeholder='Введите'
                      name='menuName'
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.menuName}
                      className={
                        errors.menuName && touched.menuName
                          ? `${s.inputError} ${styles.input}`
                          : styles.input
                      }
                    />
                    {touched.menuName && errors.menuName && (
                      <p className={s.errorValidation}>{errors.menuName}</p>
                    )}
                  </div>
                </div>

                <div className={styles.inputBlock}>
                  <div className={styles.leftSide}>
                    <h4>Описание</h4>
                  </div>
                  <div className={styles.rightSide}>
                    <Field
                      placeholder='Введите'
                      as={CustomTextArea}
                      name='description'
                      withoutFile
                      value={values.description}
                    />
                  </div>
                </div>

                <div className={styles.inputBlock}>
                  <div className={styles.leftSide}>
                    <h4>Тип категории</h4>
                  </div>
                  <div className={styles.rightSide}>
                    <Select
                      className={styles.reactSelect}
                      classNamePrefix='react-select'
                      name='categoryType'
                      options={CATEGORY_TYPES}
                      value={
                        CATEGORY_TYPES.find((type) => type.value === values.categoryType) || null
                      }
                      onChange={(selectedOption) => {
                        setFieldValue('categoryType', selectedOption ? selectedOption.value : '')
                      }}
                      placeholder='Выберите тип'
                      isClearable
                      menuPlacement='bottom'
                      menuPosition='absolute'
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          border: 'none',
                          boxShadow: 'none',
                          minHeight: 'auto',
                          backgroundColor: 'transparent',
                          height: 23,
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 5,
                          right: 0,
                        }),
                        input: (provided) => ({
                          ...provided,
                          margin: 0,
                          padding: 0,
                          height: isMobile ? 44 : 24,
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: 0,
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          padding: 0,
                        }),
                        clearIndicator: (provided) => ({
                          ...provided,
                          padding: 0,
                        }),
                        indicatorSeparator: () => ({
                          display: 'none',
                        }),
                      }}
                    />
                    {touched.categoryType && errors.categoryType && (
                      <p className={s.errorValidation}>{String(errors.categoryType)}</p>
                    )}
                  </div>
                </div>

                {userRole === 'superAdmin' && (
                  <div className={styles.inputBlock}>
                    <div className={styles.leftSide}>Компания</div>
                    <div className={styles.rightSide}>
                      <Select
                        className={styles.reactSelect}
                        classNamePrefix='react-select'
                        value={
                          values.companyId &&
                          companies.companies.find((c) => c.id === values.companyId)?.name
                            ? {
                                value: values.companyId,
                                label:
                                  companies.companies.find((c) => c.id === values.companyId)
                                    ?.name || '',
                              }
                            : null
                        }
                        options={companies.companies.map((company) => ({
                          value: company.id,
                          label: company.name,
                        }))}
                        onChange={(selectedOption) => {
                          const selectedCompanyId = selectedOption ? selectedOption.value : null
                          setCompanyId(selectedCompanyId ?? 0)
                          setFieldValue('companyId', selectedCompanyId)
                        }}
                        name='companyId'
                        isClearable
                        placeholder='Выберите компанию'
                        noOptionsMessage={() => 'Компания не найдена'}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            border: 'none',
                            boxShadow: 'none',
                            minHeight: 'auto',
                            backgroundColor: 'transparent',
                            height: 23,
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 5,
                            right: 0,
                          }),
                          input: (provided) => ({
                            ...provided,
                            margin: 0,
                            padding: 0,
                            height: isMobile ? 44 : 24,
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          clearIndicator: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          indicatorSeparator: () => ({
                            display: 'none',
                          }),
                        }}
                      />
                      {touched.companyId && errors.companyId && (
                        <div className={styles.error}>{errors.companyId}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.footerContainer}>
                {isEditMode ? (
                  <CustomButton
                    style={{ color: 'rgba(243, 92, 69, 1)' }}
                    onClick={() => setVisibleDelete(true)}
                    iconSrc={IconTrashRed}
                    className={styles.deleteBtn}
                    title={'Удалить категорию'}
                  />
                ) : (
                  <div></div>
                )}
                <CustomButton
                  className={styles.addSiteBtn}
                  title={isEditMode ? 'Сохранить' : 'Создать'}
                  type='submit'
                />
              </div>
            </Form>
          )}
        </Formik>
        {error && <p className={s.errorValidation}>{error}</p>}
      </div>
    </div>
  )
}

export default CreateCategoryModal
